import {useEffect, useMemo} from 'react';

import {safeLocalStorage} from '../../../../../util/localStorage';
import {useRampFlagSemanticLegend} from '../../../../../util/rampFeatureFlags';
import {SemanticLegendSettings} from '../../types';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from '../SemanticLegendDefaults';
import {useMetricStatisticsAggregation} from './useMetricStatisticsAggregation';
import {useMetricStatisticsForRun} from './useMetricStatisticsForRun';
import {useSemanticLegendSettings} from './useSemanticLegendSettings';

export const useSemanticLegendSettingsQueryProps = ({
  createSemanticLegendSettings,
  entityName,
  filters,
  projectName,
  updateSemanticLegendSettings,
  viewID,
  viewName,
}: {
  createSemanticLegendSettings: (
    settings: Partial<SemanticLegendSettings>
  ) => void;
  entityName: string;
  filters: string;
  projectName: string;
  updateSemanticLegendSettings: (
    settings: Partial<SemanticLegendSettings>
  ) => void;
  viewID: string;
  viewName: string;
}) => {
  const isSemanticLegendEnabled = useRampFlagSemanticLegend(entityName);

  const {
    constructedSemanticLegendData,
    currentMetric,
    semanticLegendSettingsError,
    semanticLegendSettingsLoading,
    shouldSkipMetricStatistics,
  } = useSemanticLegendSettings(viewID, isSemanticLegendEnabled);

  const {
    metricStatisticsAggregation,
    metricStatisticsError,
    metricStatisticsLoading,
  } = useMetricStatisticsAggregation({
    currentMetric,
    entityName,
    projectName,
    shouldSkipMetricStatistics,
  });

  const {
    metricStatisticsForRun,
    metricStatisticsForRunError,
    metricStatisticsForRunLoading,
  } = useMetricStatisticsForRun({
    currentMetric,
    entityName,
    filters,
    projectName,
    shouldSkip: shouldSkipMetricStatistics,
  });

  useEffect(
    () => {
      /**
       * Only when the metric statistics aggregation has changed, we need to update the semantic legend settings.
       */
      if (viewID) {
        updateSemanticLegendSettings({
          ...constructedSemanticLegendData,
          metricStatistics: metricStatisticsAggregation,
          shouldRecalculateBuckets: true,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metricStatisticsAggregation]
  );

  const combinedError = useMemo(
    () =>
      semanticLegendSettingsError ||
      metricStatisticsError ||
      metricStatisticsForRunError,
    [
      semanticLegendSettingsError,
      metricStatisticsError,
      metricStatisticsForRunError,
    ]
  );

  const combinedLoading = useMemo(
    () =>
      semanticLegendSettingsLoading ||
      metricStatisticsLoading ||
      metricStatisticsForRunLoading,
    [
      semanticLegendSettingsLoading,
      metricStatisticsLoading,
      metricStatisticsForRunLoading,
    ]
  );

  // Retrieve the semantic legend settings
  const {data: semanticLegendSettingsData, specPreviouslyCreated} =
    useMemo(() => {
      const base = {
        error: combinedError,
        loading: combinedLoading,
      };

      // If a valid semantic legend settings object exists, use it
      // we can use `currentMetric` to check this since we only have it when it destructures properly off a well-formed response
      if (currentMetric) {
        return {
          ...base,
          data: {
            metricStatistics: metricStatisticsAggregation,
            metricStatisticsForRun,
            ...constructedSemanticLegendData,
            viewID: viewID ?? '',
          },
          specPreviouslyCreated: true,
        };
      }
      // if no semantic legend settings object exists, check local storage for a saved semantic legend settings object
      const semanticLegendFromStorage = safeLocalStorage.getItem(
        `semanticLegendSettings-${viewName}`
      );
      if (semanticLegendFromStorage) {
        const parsedSemanticLegendFromStorage = {
          ...JSON.parse(semanticLegendFromStorage),
          viewID: viewID ?? '',
        };
        // if the view id is not empty and we're not waiting for any new data to load,
        // create the semantic legend settings from the stored object in local storage for the current view
        if (viewID && !semanticLegendSettingsLoading) {
          createSemanticLegendSettings(parsedSemanticLegendFromStorage);
          // remove the saved semantic legend settings from local storage because it's now stored in the backend
          safeLocalStorage.removeItem(`semanticLegendSettings-${viewName}`);
        }
        return {
          ...base,
          data: parsedSemanticLegendFromStorage,
          specPreviouslyCreated: true,
        };
      }

      // default to the default semantic legend settings
      return {
        ...base,
        data: {
          ...DEFAULT_SEMANTIC_LEGEND_SETTINGS,
          viewID: viewID ?? '',
        },
        specPreviouslyCreated: false,
      };
    }, [
      combinedError,
      combinedLoading,
      constructedSemanticLegendData,
      createSemanticLegendSettings,
      currentMetric,
      metricStatisticsAggregation,
      metricStatisticsForRun,
      semanticLegendSettingsLoading,
      viewID,
      viewName,
    ]);

  return useMemo(
    () => ({
      semanticLegendSettingsData,
      semanticLegendSettingsLoading,
      semanticLegendSettingsError,
      specPreviouslyCreated,
    }),
    [
      semanticLegendSettingsData,
      semanticLegendSettingsLoading,
      semanticLegendSettingsError,
      specPreviouslyCreated,
    ]
  );
};
